/* Table paginator (print.js tablePages): splits rows over sheets using row heights in mm.
 * Keeps the summary (totals + signatures) on a page that still has rows — never a
 * totals-only page. `adj` is the auto-fit factor (prFit) that grows row height when a
 * rendered page overflows. */
import type { Dims } from "./papers";
import type { HeadKind, SizeKind } from "./state";

export const ROW_MM: Record<SizeKind, number> = { small: 5.4, normal: 6.5, large: 7.8 };
export const FONT_PT: Record<SizeKind, number> = { small: 7.6, normal: 9, large: 10.5 };
export const HEAD_MM: Record<HeadKind, number> = { full: 30, simple: 14, pre: 42, none: 0 };

export interface PaginateOpts { d: Dims; size: SizeKind; head: HeadKind; adj?: number; summaryH?: number; subtotal?: boolean; extraFirst?: number }

export const rowsPerPage = (avail: number, rowH: number) => Math.max(1, Math.floor(avail / rowH));

export function paginate<R>(rows: R[], o: PaginateOpts): R[][] {
  const d = o.d, rh = ROW_MM[o.size] * (o.adj || 1);
  const headH = HEAD_MM[o.head], titleH = 16, runH = 9, thH = rh + 1.5, footH = 9, sumH = o.summaryH || 0;
  const pageH = d.h || 297;
  const availFirst = pageH - 2 * d.m - footH - headH - titleH - thH - (o.extraFirst || 0);
  const availOther = pageH - 2 * d.m - footH - runH - thH;
  const pages: R[][] = [];
  const first = rowsPerPage(availFirst, rh);
  pages.push(rows.slice(0, first));
  let i = first;
  const per = rowsPerPage(availOther - (o.subtotal ? rh : 0), rh);
  while (i < rows.length) { pages.push(rows.slice(i, i + per)); i += per; }
  const last = pages[pages.length - 1];
  const lastAvail = (pages.length === 1 ? availFirst : availOther) - last.length * rh;
  if (sumH && lastAvail < sumH) {
    const need = Math.min(last.length, Math.max(1, Math.ceil((sumH - lastAvail) / rh) + 1));
    pages.push(last.splice(last.length - need, need));
    if (!last.length) pages.splice(pages.length - 2, 1);
  }
  return pages;
}
